import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  confirm,
  Input,
  Label,
  Modal,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  toast,
} from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  Building2,
  CheckCircle2,
  CreditCard,
  Loader2,
  MoreHorizontal,
  PencilIcon,
  PlusIcon,
  ReceiptText,
  ScrollText,
  TrashIcon,
  Upload,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  QuotationInvoiceDocumentPaymentDTO,
  QuotationInvoicePaymentKind,
} from "@/modules/quotation-invoice/domain/types";
import { config } from "@/shared/lib/config";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useNavigate } from "@tanstack/react-router";
import {
  useConvertInvoiceToReceipt,
  useCreateDocumentPayment,
  useDeleteDocumentPayment,
  useUpdateDocumentPayment,
} from "../api/queries";
import { computeNetReceived } from "../lib/compute-net-received";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { uploadPaymentSlipFile } from "../lib/upload-payment-slip";

function fileUrl(fileKey: string): string {
  return `${config.apiUrl}/files/${fileKey}`;
}

const KIND_OPTIONS: { value: QuotationInvoicePaymentKind; label: string }[] = [
  { value: "deposit", label: "ມັດຈຳ" },
  { value: "partial", label: "ຊຳລະບາງສ່ວນ" },
  { value: "final", label: "ຊຳລະຄົບ" },
  { value: "refund", label: "ຄືນເງິນ" },
];

/** ຄ່າທີ່ບັນທຶກໃນ DB (method) — ສະແດງເປັນ radio + icon */
const PAYMENT_METHOD_OPTIONS: {
  value: string;
  label: string;
  Icon: LucideIcon;
}[] = [
  { value: "bank_transfer", label: "ໂອນເງິນ", Icon: Building2 },
  { value: "cash", label: "ເງິນສົດ", Icon: Banknote },
  { value: "card", label: "ບັດ / QR", Icon: CreditCard },
  { value: "cheque", label: "ເຊັກ", Icon: ScrollText },
  { value: "other", label: "ອື່ນ", Icon: MoreHorizontal },
];

const DEFAULT_PAYMENT_METHOD =
  PAYMENT_METHOD_OPTIONS[0]?.value ?? "bank_transfer";

function normalizePaymentMethod(raw: string | null | undefined): string {
  if (!raw?.trim()) return DEFAULT_PAYMENT_METHOD;
  const v = raw.trim().toLowerCase();
  if (PAYMENT_METHOD_OPTIONS.some((o) => o.value === v)) return v;
  return "other";
}

function paymentMethodLabel(stored: string | null | undefined): string {
  if (!stored?.trim()) return "—";
  const v = stored.trim().toLowerCase();
  const hit = PAYMENT_METHOD_OPTIONS.find((o) => o.value === v);
  return hit?.label ?? stored;
}

type DocumentPaymentsSectionProps = {
  documentId: string;
  /** ບັນທຶກການຊຳລະໃຊ້ໄດ້ສະເພາະໃບບິນ */
  documentType: string;
  currency: string;
  documentTotal: number;
  status: string;
  payments: QuotationInvoiceDocumentPaymentDTO[];
};

/** ສະລິບທີ່ບັນທຶກໃນ DB ແລ້ວ vs ໄຟລ໌ທີ່ເລືອກລໍຖ້າອັບໂຫຼດຕອນກົດບັນທຶກ */
type PaymentSlipRow =
  | { source: "server"; fileKey: string; fileName: string }
  | { source: "local"; id: string; file: File };

function toYyyyMmDd(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const AMOUNT_EPS = 0.005;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** ຄ່າເລີ່ມຕົ້ນຊ່ອງຈຳນວນ: ຄັ້ງທຳອິດ = ຍອດລວມ invoice; ຖ້າມີການຊຳລະແລ້ວ = ຍອດເຫຼືອ */
function defaultAmountStringForNewPayment(
  documentTotal: number,
  remaining: number,
  hasPriorPayments: boolean,
): string {
  const n = hasPriorPayments ? remaining : documentTotal;
  if (!Number.isFinite(n) || n <= 0) return "";
  const r = round2(n);
  return r.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function inferPaymentKind(
  isRefund: boolean,
  documentTotal: number,
  projectedNetAfter: number,
): QuotationInvoicePaymentKind {
  if (isRefund) return "refund";
  if (documentTotal <= 0) return "partial";
  if (projectedNetAfter >= documentTotal - AMOUNT_EPS) return "final";
  return "partial";
}

/** ຮອງຮັບປ້ອນແບບມີ comma (ເຊັນ 2,200) ແລະຕັດຊ່ອງຫວ່າງ */
function parsePaymentAmount(raw: string): number | null {
  const s = raw.trim().replace(/,/g, "");
  if (s === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function projectedNetAfterSave(
  payments: QuotationInvoiceDocumentPaymentDTO[],
  editPaymentId: string | undefined,
  kind: QuotationInvoicePaymentKind,
  amount: number,
): number {
  const others = editPaymentId
    ? payments.filter((p) => p.id !== editPaymentId)
    : payments;
  const base = computeNetReceived(
    others.map((p) => ({ kind: p.kind, amount: p.amount })),
  );
  const delta = kind === "refund" ? -amount : amount;
  return Math.round((base + delta) * 100) / 100;
}

function PaymentMethodRadios({
  method,
  setMethod,
}: {
  method: string;
  setMethod: (s: string) => void;
}) {
  const gid = useId();
  const safe = PAYMENT_METHOD_OPTIONS.some((o) => o.value === method)
    ? method
    : DEFAULT_PAYMENT_METHOD;

  useEffect(() => {
    if (method !== safe) setMethod(safe);
  }, [method, safe, setMethod]);

  return (
    <div className="space-y-2">
      <p className="font-medium text-sm" id={`${gid}-legend`}>
        ວິທີຊຳລະ
      </p>
      <RadioGroup
        value={safe}
        onValueChange={setMethod}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
        aria-labelledby={`${gid}-legend`}
      >
        {PAYMENT_METHOD_OPTIONS.map((opt) => {
          const id = `${gid}-${opt.value}`;
          const Icon = opt.Icon;
          const selected = safe === opt.value;
          return (
            <div key={opt.value} className="relative">
              <RadioGroupItem
                value={opt.value}
                id={id}
                className="peer sr-only"
              />
              <Label
                htmlFor={id}
                className={
                  selected
                    ? "flex cursor-pointer items-center gap-3 rounded-xl border-2 border-primary bg-primary/5 p-3 shadow-md ring-2 ring-primary/20 transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-ring peer-focus-visible:outline-offset-2"
                    : "flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/40 peer-focus-visible:outline-2 peer-focus-visible:outline-ring peer-focus-visible:outline-offset-2"
                }
              >
                <span
                  className={
                    selected
                      ? "flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"
                      : "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                  }
                  aria-hidden
                >
                  <Icon className="size-5" strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1 font-medium text-sm leading-snug">
                  {opt.label}
                </span>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export function DocumentPaymentsSection({
  documentId,
  documentType,
  currency,
  documentTotal,
  status,
  payments,
}: DocumentPaymentsSectionProps) {
  const canUpdate = useActionPermission(["quotation_invoice:update"]);
  const createPayment = useCreateDocumentPayment(documentId);
  const updatePayment = useUpdateDocumentPayment(documentId);
  const deletePayment = useDeleteDocumentPayment(documentId);
  const convertToReceipt = useConvertInvoiceToReceipt(documentId);
  const nav = useNavigate();

  const [addOpen, setAddOpen] = useState(false);
  const [editPayment, setEditPayment] =
    useState<QuotationInvoiceDocumentPaymentDTO | null>(null);

  const [isRefund, setIsRefund] = useState(false);
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [slipRows, setSlipRows] = useState<PaymentSlipRow[]>([]);
  const [uploadingSlips, setUploadingSlips] = useState(false);

  const resetForm = useCallback(() => {
    setIsRefund(false);
    setAmount("");
    setPaidAt("");
    setMethod(DEFAULT_PAYMENT_METHOD);
    setNotes("");
    setSlipRows([]);
  }, []);

  const net = computeNetReceived(
    payments.map((p) => ({ kind: p.kind, amount: p.amount })),
  );
  const remaining = Math.round((documentTotal - net) * 100) / 100;
  const fmt = (n: number) => formatDocumentCurrency(n, currency);

  const baseNetForPaymentLimit = editPayment
    ? computeNetReceived(
        payments
          .filter((p) => p.id !== editPayment.id)
          .map((p) => ({ kind: p.kind, amount: p.amount })),
      )
    : net;
  const maxReceiveThisEntry =
    documentTotal > 0
      ? Math.round((documentTotal - baseNetForPaymentLimit) * 100) / 100
      : null;

  const amountLimitHint =
    documentTotal > 0 && !isRefund && maxReceiveThisEntry != null
      ? `ສູງສຸດຕາມຍອດເຫຼືອ: ${fmt(maxReceiveThisEntry)}`
      : null;

  const prevAddOpen = useRef(false);
  useEffect(() => {
    const justOpened = addOpen && !prevAddOpen.current;
    prevAddOpen.current = addOpen;
    if (!justOpened || editPayment) return;
    resetForm();
    const net0 = computeNetReceived(
      payments.map((p) => ({ kind: p.kind, amount: p.amount })),
    );
    const remaining0 = Math.round((documentTotal - net0) * 100) / 100;
    setAmount(
      defaultAmountStringForNewPayment(
        documentTotal,
        remaining0,
        payments.length > 0,
      ),
    );
  }, [addOpen, editPayment, documentTotal, payments, resetForm]);

  useEffect(() => {
    if (editPayment) {
      setIsRefund(editPayment.kind === "refund");
      setAmount(
        round2(editPayment.amount).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
      );
      setPaidAt(toYyyyMmDd(editPayment.paidAt));
      setMethod(normalizePaymentMethod(editPayment.method));
      setNotes(editPayment.notes ?? "");
      setSlipRows(
        editPayment.attachments.map((a) => ({
          source: "server" as const,
          fileKey: a.fileKey,
          fileName: a.fileName,
        })),
      );
    }
  }, [editPayment]);

  const canRecord =
    documentType === "invoice" &&
    canUpdate &&
    (status === "issued" || status === "partially_paid" || status === "paid");

  /** ຮັບຄົບຕາມຍອດເອກະສານ — ບໍ່ໃຫ້ບັນທຶກ/ແກ້/ລຶບການຊຳລະເພີ່ມ */
  const isFullyPaid =
    status === "paid" || (documentTotal > 0 && remaining <= AMOUNT_EPS);

  const canModifyPayments = canRecord && !isFullyPaid;

  useEffect(() => {
    if (!isFullyPaid) return;
    setAddOpen(false);
    setEditPayment(null);
  }, [isFullyPaid]);

  const buildAttachmentsPayload = useCallback(async () => {
    const out: { fileKey: string; fileName: string }[] = [];
    for (const row of slipRows) {
      if (row.source === "server") {
        out.push({ fileKey: row.fileKey, fileName: row.fileName });
      } else {
        const up = await uploadPaymentSlipFile(row.file, documentId);
        out.push(up);
      }
    }
    return out;
  }, [slipRows, documentId]);

  const submitCreate = async () => {
    if (isFullyPaid) {
      toast.error("ຊຳລະຄົບແລ້ວ — ບໍ່ສາມາດບັນທຶກເພີ່ມ");
      return;
    }
    const n = parsePaymentAmount(amount);
    if (n == null) {
      toast.error("ປ້ອນຈຳນວນເງິນທີ່ຖືກຕ້ອງ (ຕົວເລກບວກ, ສາມາດໃຊ້ comma ຄັ້ນໜ່ຽວ)");
      return;
    }
    const kindForProjection: QuotationInvoicePaymentKind = isRefund
      ? "refund"
      : "partial";
    const projected = projectedNetAfterSave(
      payments,
      undefined,
      kindForProjection,
      n,
    );
    const kind = inferPaymentKind(isRefund, documentTotal, projected);
    if (documentTotal > 0) {
      if (projected > documentTotal + AMOUNT_EPS) {
        toast.error(
          !isRefund && maxReceiveThisEntry != null
            ? `ຍອດບໍ່ເກີນ ${fmt(maxReceiveThisEntry)} ໄດ້ (ຍອດເອກະສານ ${fmt(documentTotal)}, ເຫຼືອຕາມລາຍການ ${fmt(remaining)})`
            : `ຍອດຮັບສະສົມຄົບຫຼັງບັນທຶກຕ້ອງບໍ່ເກີນຍອດເອກະສານ (${fmt(documentTotal)})`,
        );
        return;
      }
    }
    setUploadingSlips(true);
    let attachments: { fileKey: string; fileName: string }[];
    try {
      attachments = await buildAttachmentsPayload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ອັບໂຫຼດສະລິບລົ້ມເຫຼວ");
      setUploadingSlips(false);
      return;
    }
    try {
      await createPayment.mutateAsync({
        kind,
        amount: n,
        paidAt: paidAt.trim() || null,
        method: normalizePaymentMethod(method),
        notes: notes.trim() || null,
        attachments,
      });
      setAddOpen(false);
      resetForm();
    } finally {
      setUploadingSlips(false);
    }
  };

  const submitUpdate = async () => {
    if (!editPayment) return;
    if (isFullyPaid) {
      toast.error("ຊຳລະຄົບແລ້ວ — ບໍ່ສາມາດແກ້ໄຂລາຍການນີ້");
      return;
    }
    const n = parsePaymentAmount(amount);
    if (n == null) {
      toast.error("ປ້ອນຈຳນວນເງິນທີ່ຖືກຕ້ອງ (ຕົວເລກບວກ, ສາມາດໃຊ້ comma ຄັ້ນໜ່ຽວ)");
      return;
    }
    const kindForProjection: QuotationInvoicePaymentKind = isRefund
      ? "refund"
      : "partial";
    const projected = projectedNetAfterSave(
      payments,
      editPayment.id,
      kindForProjection,
      n,
    );
    const kind = inferPaymentKind(isRefund, documentTotal, projected);
    if (documentTotal > 0) {
      if (projected > documentTotal + AMOUNT_EPS) {
        const remBeforeThisRow =
          Math.round((documentTotal - baseNetForPaymentLimit) * 100) / 100;
        toast.error(
          !isRefund && maxReceiveThisEntry != null
            ? `ຍອດບໍ່ເກີນ ${fmt(maxReceiveThisEntry)} ໄດ້ (ຍອດເອກະສານ ${fmt(documentTotal)}, ເຫຼືອກ່ອນລາຍການນີ້ ${fmt(remBeforeThisRow)})`
            : `ຍອດຮັບສະສົມຄົບຫຼັງບັນທຶກຕ້ອງບໍ່ເກີນຍອດເອກະສານ (${fmt(documentTotal)})`,
        );
        return;
      }
    }
    setUploadingSlips(true);
    let attachments: { fileKey: string; fileName: string }[];
    try {
      attachments = await buildAttachmentsPayload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ອັບໂຫຼດສະລິບລົ້ມເຫຼວ");
      setUploadingSlips(false);
      return;
    }
    try {
      await updatePayment.mutateAsync({
        paymentId: editPayment.id,
        input: {
          kind,
          amount: n,
          paidAt: paidAt.trim() || null,
          method: normalizePaymentMethod(method),
          notes: notes.trim() || null,
          attachments,
        },
      });
      setEditPayment(null);
      resetForm();
    } finally {
      setUploadingSlips(false);
    }
  };

  const handleDelete = async (p: QuotationInvoiceDocumentPaymentDTO) => {
    const ok = await confirm({
      title: "ລຶບການຊຳລະນີ້?",
      description: `${KIND_OPTIONS.find((o) => o.value === p.kind)?.label ?? p.kind} ${fmt(p.amount)}`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) deletePayment.mutate(p.id);
  };

  const addSlipFiles = (files: FileList | null) => {
    if (!files?.length) return;
    setSlipRows((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({
        source: "local" as const,
        id: nanoid(),
        file,
      })),
    ]);
  };

  const removeSlipRow = (idx: number) => {
    setSlipRows((prev) => prev.filter((_, i) => i !== idx));
  };

  if (status === "draft" || status === "cancelled") {
    return null;
  }

  if (documentType !== "invoice") {
    return null;
  }

  return (
    <>
      <Card data-tourid="document-payments">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="font-semibold text-base">
            ການຊຳລະ / ມັດຈຳ
          </CardTitle>
          {canModifyPayments && (
            <Button
              type="button"
              size="sm"
              className="gap-1"
              onClick={() => {
                resetForm();
                setAddOpen(true);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              ບັນທຶກການຊຳລະ
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">ຍອດເອກະສານ</span>
              <p className="font-medium">{fmt(documentTotal)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">ຮັບແລ້ວ (ສຸດທິ)</span>
              <p className="font-medium">{fmt(net)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">ເຫຼືອ</span>
              <p className="font-medium">{fmt(remaining)}</p>
            </div>
          </div>

          {isFullyPaid && (
            <p className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-muted-foreground text-sm">
              ຊຳລະຄົບຕາມຍອດເອກະສານແລ້ວ — ບໍ່ສາມາດບັນທຶກການຊຳລະເພີ່ມ ຫຼື ແກ້ໄຂລາຍການ
            </p>
          )}

          {payments.length === 0 ? (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີການບັນທຶກຊຳລະ</p>
          ) : (
            <ul className="space-y-3">
              {payments.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-start justify-between gap-2 border-b pb-3 last:border-0"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium">
                      {KIND_OPTIONS.find((o) => o.value === p.kind)?.label ??
                        p.kind}{" "}
                      <span className="text-primary">{fmt(p.amount)}</span>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {p.paidAt ? formatDateLocal(p.paidAt) : "—"} ·{" "}
                      {p.recordedByName ?? p.recordedBy}
                      {p.method?.trim()
                        ? ` · ${paymentMethodLabel(p.method)}`
                        : ""}
                    </p>
                    {p.notes && (
                      <p className="text-muted-foreground text-sm">{p.notes}</p>
                    )}
                    {p.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {p.attachments.map((a) => (
                          <a
                            key={a.id}
                            href={fileUrl(a.fileKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={fileUrl(a.fileKey)}
                              alt={a.fileName}
                              className="h-16 w-16 rounded border object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {p.receiptId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={() =>
                          void nav({
                            to: "/app/documents/$id",
                            params: { id: p.receiptId! },
                          })
                        }
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {p.receiptNumber ?? "ໃບຮັບເງິນ"}
                      </Button>
                    ) : (
                      canUpdate && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1 text-xs"
                          disabled={convertToReceipt.isPending}
                          onClick={() =>
                            void convertToReceipt.mutateAsync({
                              id: documentId,
                              paymentId: p.id,
                            })
                          }
                        >
                          {convertToReceipt.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ReceiptText className="h-3.5 w-3.5" />
                          )}
                          ອອກໃບຮັບ
                        </Button>
                      )
                    )}
                    {canModifyPayments && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setEditPayment(p)}
                          aria-label="ແກ້ໄຂ"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(p)}
                          aria-label="ລຶບ"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title="ບັນທຶກການຊຳລະ"
        size="lg"
      >
        <PaymentFormFields
          isRefund={isRefund}
          setIsRefund={setIsRefund}
          amount={amount}
          setAmount={setAmount}
          amountLimitHint={amountLimitHint}
          paidAt={paidAt}
          setPaidAt={setPaidAt}
          method={method}
          setMethod={setMethod}
          notes={notes}
          setNotes={setNotes}
          slipRows={slipRows}
          addSlipFiles={addSlipFiles}
          removeSlipRow={removeSlipRow}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setAddOpen(false)}
            disabled={uploadingSlips || createPayment.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void submitCreate()}
            disabled={uploadingSlips || createPayment.isPending}
          >
            {uploadingSlips || createPayment.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ກຳລັງບັນທຶກ...
              </>
            ) : (
              "ບັນທຶກ"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!editPayment}
        onOpenChange={(o) => !o && setEditPayment(null)}
        title="ແກ້ໄຂການຊຳລະ"
        size="lg"
      >
        <PaymentFormFields
          isRefund={isRefund}
          setIsRefund={setIsRefund}
          amount={amount}
          setAmount={setAmount}
          amountLimitHint={amountLimitHint}
          paidAt={paidAt}
          setPaidAt={setPaidAt}
          method={method}
          setMethod={setMethod}
          notes={notes}
          setNotes={setNotes}
          slipRows={slipRows}
          addSlipFiles={addSlipFiles}
          removeSlipRow={removeSlipRow}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setEditPayment(null)}
            disabled={uploadingSlips || updatePayment.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void submitUpdate()}
            disabled={uploadingSlips || updatePayment.isPending}
          >
            {uploadingSlips || updatePayment.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ກຳລັງບັນທຶກ...
              </>
            ) : (
              "ບັນທຶກ"
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}

function PaymentFormFields({
  isRefund,
  setIsRefund,
  amount,
  setAmount,
  amountLimitHint,
  paidAt,
  setPaidAt,
  method,
  setMethod,
  notes,
  setNotes,
  slipRows,
  addSlipFiles,
  removeSlipRow,
}: {
  isRefund: boolean;
  setIsRefund: (v: boolean) => void;
  amount: string;
  setAmount: (s: string) => void;
  amountLimitHint: string | null;
  paidAt: string;
  setPaidAt: (s: string) => void;
  method: string;
  setMethod: (s: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  slipRows: PaymentSlipRow[];
  addSlipFiles: (files: FileList | null) => void;
  removeSlipRow: (idx: number) => void;
}) {
  const slipInputId = useId();
  const refundCbId = useId();
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="pay-amount">ຈຳນວນເງິນ</Label>
          <Input
            id="pay-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 h-9"
          />
          {amountLimitHint && (
            <p className="mt-1 text-muted-foreground text-xs">
              {amountLimitHint}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="pay-date">ວັນທີຊຳລະ</Label>
          <Input
            id="pay-date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            className="mt-1 h-9"
          />
        </div>
      </div>
      <PaymentMethodRadios method={method} setMethod={setMethod} />
      <div className="flex items-center gap-2">
        <Checkbox
          id={refundCbId}
          checked={isRefund}
          onCheckedChange={(v) => setIsRefund(Boolean(v))}
        />
        <label htmlFor={refundCbId} className="text-sm">
          ຄືນເງິນ (ຫັກຈາກຍອດຮັບ)
        </label>
      </div>
      <div>
        <Label htmlFor="pay-notes">ຫມາຍເຫດ</Label>
        <Textarea
          id="pay-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 min-h-[72px]"
          rows={2}
        />
      </div>
      <div>
        <Label htmlFor={slipInputId}>ສະລິບໂອນ (ຖ້າມີ)</Label>
        <input
          id={slipInputId}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addSlipFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => document.getElementById(slipInputId)?.click()}
          >
            <Upload className="h-4 w-4" />
            ເລືອກໄຟລ໌
          </Button>
        </div>
        <p className="mt-1 text-muted-foreground text-xs">
          ອັບໂຫຼດໄປຄັງເມື່ອກົດບັນທຶກ — ສາມາດເລືອກຫຼາຍໄຟລ໌
        </p>
        {slipRows.length > 0 && (
          <ul className="mt-2 space-y-1 text-sm">
            {slipRows.map((row, i) => (
              <li
                key={
                  row.source === "server"
                    ? `s-${row.fileKey}-${i}`
                    : `l-${row.id}`
                }
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-muted-foreground">
                  {row.source === "server" ? row.fileName : row.file.name}
                  {row.source === "local" && (
                    <span className="ml-2 text-xs opacity-80">(ລໍຖ້າອັບໂຫຼດ)</span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSlipRow(i)}
                >
                  ລຶບ
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
